// Utility for automatic internal link building.
// Replaces the FIRST occurrence of each anchor with an <a> tag, skipping matches
// inside existing tags or anchors. Works on HTML strings produced by `marked`.

export type LinkRule = { anchor: string; url: string; priority?: number };

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function applyInternalLinks(html: string, rules: LinkRule[]): string {
  if (!html || rules.length === 0) return html;
  // Sort by priority desc, then by anchor length desc (longer/more specific first).
  const sorted = [...rules].sort((a, b) => {
    const p = (b.priority ?? 0) - (a.priority ?? 0);
    if (p !== 0) return p;
    return b.anchor.length - a.anchor.length;
  });
  const used = new Set<string>();
  let out = html;
  for (const { anchor, url } of sorted) {
    const key = anchor.toLowerCase();
    if (used.has(key)) continue;
    // Only accept relative paths or https URLs. Anything else is dropped.
    if (!/^(\/[^\s"'<>]*|https:\/\/[^\s"'<>]+)$/i.test(url)) continue;
    const safeUrl = url
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const escaped = escapeRegExp(anchor);
    // Negative lookahead avoids replacements inside existing tags or anchors.
    const re = new RegExp(
      `(?<![<\\w/])(${escaped})(?![\\w-])(?![^<]*>)(?![^<>]*</a>)`,
      "i",
    );
    if (re.test(out)) {
      out = out.replace(
        re,
        `<a href="${safeUrl}" class="text-primary underline-offset-4 hover:underline">$1</a>`,
      );
      used.add(key);
    }
  }
  return out;
}

export function extractYouTubeId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1) || null;
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname === "/watch") return u.searchParams.get("v");
      if (u.pathname.startsWith("/embed/")) return u.pathname.split("/")[2] || null;
      if (u.pathname.startsWith("/shorts/")) return u.pathname.split("/")[2] || null;
    }
    return null;
  } catch {
    return null;
  }
}

function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/<[^>]+>/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80);
}

/**
 * Injects an `id` attribute (when absent) into every <h2> and <h3> in the HTML
 * so they can be linked from a Table of Contents.
 */
export function addHeadingIds(html: string): string {
  if (!html) return html;
  const used = new Set<string>();
  return html.replace(/<(h[23])([^>]*)>([\s\S]*?)<\/\1>/gi, (full, tag, attrs, inner) => {
    if (/\sid=/.test(attrs)) return full;
    const text = inner.replace(/<[^>]+>/g, "").trim();
    if (!text) return full;
    let base = slugifyHeading(text) || tag;
    let id = base;
    let n = 2;
    while (used.has(id)) id = `${base}-${n++}`;
    used.add(id);
    return `<${tag}${attrs} id="${id}">${inner}</${tag}>`;
  });
}

export type Heading = { level: 2 | 3; id: string; text: string };

/**
 * Extracts h2/h3 entries from HTML for rendering a TOC. Assumes ids were
 * already injected (e.g. by `addHeadingIds`).
 */
export function extractHeadings(html: string): Heading[] {
  if (!html) return [];
  const out: Heading[] = [];
  const re = /<(h2|h3)\b[^>]*\sid="([^"]+)"[^>]*>([\s\S]*?)<\/\1>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    const text = m[3].replace(/<[^>]+>/g, "").trim();
    if (!text) continue;
    out.push({ level: m[1].toLowerCase() === "h2" ? 2 : 3, id: m[2], text });
  }
  return out;
}

/**
 * Transforms the FAQ section of an article HTML into open <details> accordions.
 * The FAQ is detected by an h2 whose text contains "FAQ" or "Perguntas Frequentes".
 * From that heading onward, each <h3> (and the content between it and the next
 * h3/h2) is wrapped in <details open><summary>...</summary>...</details>.
 */
export function transformFaqAccordions(html: string): string {
  if (!html) return html;
  const faqStart = html.search(
    /<h2[^>]*>[^<]*(?:FAQ|Perguntas\s+Frequentes)[^<]*<\/h2>/i,
  );
  if (faqStart === -1) return html;
  const before = html.slice(0, faqStart);
  const rest = html.slice(faqStart);
  // Split rest by h3 tags. First chunk is the h2 + any intro before first h3.
  const h3Re = /<h3[^>]*>([\s\S]*?)<\/h3>/gi;
  const parts: { idx: number; len: number; title: string }[] = [];
  let m: RegExpExecArray | null;
  while ((m = h3Re.exec(rest)) !== null) {
    parts.push({ idx: m.index, len: m[0].length, title: m[1] });
  }
  if (parts.length === 0) return html;
  let out = rest.slice(0, parts[0].idx);
  // Wrap each h3..(next h3 / end) block as an open accordion.
  for (let i = 0; i < parts.length; i++) {
    const start = parts[i].idx + parts[i].len;
    const end = i + 1 < parts.length ? parts[i + 1].idx : rest.length;
    const body = rest.slice(start, end);
    out +=
      `<details open class="faq-item group mt-3 rounded-xl border border-border bg-secondary/40 p-5">` +
      `<summary class="cursor-pointer list-none text-base font-semibold text-foreground flex items-start justify-between gap-4">` +
      `<span>${parts[i].title}</span>` +
      `<span aria-hidden class="mt-1 text-primary transition-transform group-open:rotate-45">+</span>` +
      `</summary><div class="mt-3 text-sm leading-relaxed text-muted-foreground">${body}</div></details>`;
  }
  return before + out;
}